import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { naytiveAPI } from '../services/api';

// Colour a channel / event family so the feed scans quickly.
const familyColor = (type = '') => {
  const fam = type.split('.')[0];
  const map = {
    page: '#6366F1', ui: '#8B94A3', visa: '#0EA5E9', incorporation: '#F59E0B',
    compliance: '#10B981', payment: '#013399', document: '#7C3AED',
    banking: '#0891B2', attorney: '#DB2777', auth: '#DC2626',
  };
  return map[fam] || '#6B7280';
};

const fmtTime = (t) => {
  if (!t) return '';
  const d = new Date(t);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const summarize = (payload) => {
  if (!payload || typeof payload !== 'object') return '';
  const p = payload;
  const bits = [];
  if (p.path) bits.push(p.path);
  if (p.label) bits.push(`“${p.label}”`);
  if (p.tag && !p.label) bits.push(`<${p.tag}>`);
  if (p.href) bits.push(p.href);
  return bits.join('  ·  ');
};

export default function Activity() {
  const [sp, setSp] = useSearchParams();
  const userId = sp.get('userId') || '';

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [tab, setTab] = useState('user'); // 'user' | 'admin'

  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [channel, setChannel] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  useEffect(() => {
    naytiveAPI.listActivityTypes()
      .then((r) => setTypes(r.data?.data || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: 50 };
      if (q) params.q = q;
      let r;
      if (tab === 'admin') {
        r = await naytiveAPI.listAdminAudit(params);
      } else {
        if (userId) params.userId = userId;
        if (type) params.type = type;
        if (channel) params.channel = channel;
        if (from) params.from = from.replace('T', ' ') + ':00';
        if (to) params.to = to.replace('T', ' ') + ':59';
        r = await naytiveAPI.listActivity(params);
      }
      setRows(r.data?.data || []);
      setPagination(r.data?.pagination || { total: 0, page: 1, totalPages: 1 });
    } catch (e) {
      setError('Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [tab, page, userId, q, type, channel, from, to]);

  useEffect(() => { load(); }, [load]);

  const resetToFirst = () => setPage(1);

  const label = { fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4, display: 'block' };
  const input = { padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, background: '#fff' };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Activity</h1>
        <span style={{ fontSize: 13, color: '#6B7280' }}>{pagination.total} events</span>
      </div>

      {/* tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid #E5E7EB' }}>
        {[['user', 'User activity'], ['admin', 'Admin actions']].map(([key, lbl]) => (
          <button key={key} onClick={() => { setTab(key); setPage(1); setExpanded(null); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              padding: '8px 14px', color: tab === key ? '#013399' : '#6B7280',
              borderBottom: tab === key ? '2px solid #013399' : '2px solid transparent', marginBottom: -1,
            }}>
            {lbl}
          </button>
        ))}
      </div>
      {tab === 'user' && userId && (
        <div style={{ marginBottom: 14, fontSize: 13 }}>
          Filtered to user <strong>#{userId}</strong>
          {rows[0]?.email ? ` — ${rows[0].email}` : ''}{'  '}
          <button onClick={() => { setSp({}); resetToFirst(); }}
            style={{ marginLeft: 8, background: 'none', border: 'none', color: '#013399', cursor: 'pointer', fontSize: 13 }}>
            clear
          </button>
        </div>
      )}

      {/* filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', margin: '12px 0 18px' }}>
        <div>
          <span style={label}>Search</span>
          <input style={{ ...input, width: 220 }} placeholder="event, email, name…" value={q}
            onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && resetToFirst()} />
        </div>
        {tab === 'user' && (
          <>
            <div>
              <span style={label}>Event type</span>
              <select style={{ ...input, width: 200 }} value={type} onChange={(e) => { setType(e.target.value); resetToFirst(); }}>
                <option value="">All types</option>
                {types.map((t) => <option key={t.event_type} value={t.event_type}>{t.event_type} ({t.c})</option>)}
              </select>
            </div>
            <div>
              <span style={label}>Channel</span>
              <select style={{ ...input, width: 120 }} value={channel} onChange={(e) => { setChannel(e.target.value); resetToFirst(); }}>
                <option value="">All</option>
                <option value="web">web</option>
                <option value="whatsapp">whatsapp</option>
                <option value="voice">voice</option>
              </select>
            </div>
            <div>
              <span style={label}>From</span>
              <input type="datetime-local" style={input} value={from} onChange={(e) => { setFrom(e.target.value); resetToFirst(); }} />
            </div>
            <div>
              <span style={label}>To</span>
              <input type="datetime-local" style={input} value={to} onChange={(e) => { setTo(e.target.value); resetToFirst(); }} />
            </div>
          </>
        )}
        <button onClick={resetToFirst}
          style={{ ...input, cursor: 'pointer', background: '#013399', color: '#fff', border: 'none', fontWeight: 600 }}>
          Apply
        </button>
      </div>

      {error && <div style={{ color: '#DC2626', marginBottom: 12 }}>{error}</div>}

      {tab === 'user' && (
      <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F9FAFB', textAlign: 'left', color: '#6B7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              <th style={{ padding: '10px 14px' }}>Time</th>
              <th style={{ padding: '10px 14px' }}>User</th>
              <th style={{ padding: '10px 14px' }}>Event</th>
              <th style={{ padding: '10px 14px' }}>Details</th>
              <th style={{ padding: '10px 14px' }}>Ch</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#9CA3AF' }}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#9CA3AF' }}>No activity</td></tr>
            ) : rows.map((r) => {
              const name = [r.first_name, r.last_name].filter(Boolean).join(' ');
              const isOpen = expanded === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr style={{ borderTop: '1px solid #F3F4F6', cursor: 'pointer' }} onClick={() => setExpanded(isOpen ? null : r.id)}>
                    <td style={{ padding: '9px 14px', whiteSpace: 'nowrap', color: '#6B7280' }}>{fmtTime(r.occurred_at)}</td>
                    <td style={{ padding: '9px 14px', whiteSpace: 'nowrap' }}>
                      {r.user_id ? (
                        <Link to={`/activity?userId=${r.user_id}`} onClick={(e) => e.stopPropagation()} style={{ color: '#013399', textDecoration: 'none' }}>
                          {name || r.email || `#${r.user_id}`}
                        </Link>
                      ) : <span style={{ color: '#9CA3AF' }}>anon</span>}
                    </td>
                    <td style={{ padding: '9px 14px', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: familyColor(r.event_type), marginRight: 7 }} />
                      <span style={{ fontWeight: 600 }}>{r.event_type}</span>
                      {r.subject_type ? <span style={{ color: '#9CA3AF' }}> · {r.subject_type}{r.subject_id ? `#${r.subject_id}` : ''}</span> : ''}
                    </td>
                    <td style={{ padding: '9px 14px', color: '#4B5563', maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {summarize(r.payload_public)}
                    </td>
                    <td style={{ padding: '9px 14px', color: '#9CA3AF' }}>{r.channel}</td>
                  </tr>
                  {isOpen && (
                    <tr style={{ background: '#FafBfC' }}>
                      <td colSpan={5} style={{ padding: '10px 14px' }}>
                        <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#374151' }}>
                          {JSON.stringify(r.payload_public, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {tab === 'admin' && (
      <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F9FAFB', textAlign: 'left', color: '#6B7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              <th style={{ padding: '10px 14px' }}>Time</th>
              <th style={{ padding: '10px 14px' }}>Admin</th>
              <th style={{ padding: '10px 14px' }}>Action</th>
              <th style={{ padding: '10px 14px' }}>Subject</th>
              <th style={{ padding: '10px 14px' }}>IP</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#9CA3AF' }}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#9CA3AF' }}>No admin actions recorded</td></tr>
            ) : rows.map((r) => {
              const isOpen = expanded === r.id;
              const hasDiff = r.diff_json && Object.keys(r.diff_json).length > 0;
              return (
                <React.Fragment key={r.id}>
                  <tr style={{ borderTop: '1px solid #F3F4F6', cursor: hasDiff ? 'pointer' : 'default' }}
                    onClick={() => hasDiff && setExpanded(isOpen ? null : r.id)}>
                    <td style={{ padding: '9px 14px', whiteSpace: 'nowrap', color: '#6B7280' }}>{fmtTime(r.created_at)}</td>
                    <td style={{ padding: '9px 14px', whiteSpace: 'nowrap' }}>{r.admin_name || r.admin_email || `#${r.admin_user_id}`}</td>
                    <td style={{ padding: '9px 14px', whiteSpace: 'nowrap', fontWeight: 600 }}>{r.action}</td>
                    <td style={{ padding: '9px 14px', color: '#4B5563' }}>
                      {r.subject_type ? `${r.subject_type}${r.subject_id ? ` #${r.subject_id}` : ''}` : ''}
                      {hasDiff ? <span style={{ color: '#9CA3AF' }}>  · diff</span> : ''}
                    </td>
                    <td style={{ padding: '9px 14px', color: '#9CA3AF' }}>{r.ip}</td>
                  </tr>
                  {isOpen && hasDiff && (
                    <tr style={{ background: '#FafBfC' }}>
                      <td colSpan={5} style={{ padding: '10px 14px' }}>
                        <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#374151' }}>
                          {JSON.stringify(r.diff_json, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {/* pagination */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
        <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
          style={{ ...input, cursor: page <= 1 ? 'default' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}>‹ Prev</button>
        <span style={{ fontSize: 13, color: '#6B7280' }}>Page {pagination.page} of {pagination.totalPages}</span>
        <button disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}
          style={{ ...input, cursor: page >= pagination.totalPages ? 'default' : 'pointer', opacity: page >= pagination.totalPages ? 0.5 : 1 }}>Next ›</button>
      </div>
    </div>
  );
}
